/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-props-no-spreading */
import { Collapse } from 'antd';
import classnames from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { ContextMenu, ContextMenuTrigger, MenuItem, connectMenu } from 'react-contextmenu';
import { UncontrolledTooltip } from 'reactstrap';
import { EVENT_COUNT_CONVERT, RR_INTERVAL_MENU, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import {
  BlackNoiseIcon as blackNoiseIcon,
  OverviewCircularProgress,
  IconArrowCollapse as arrowCollapse,
  IconArrowExpand as arrowExpand,
  UnappliedChangesIcon as unappliedChangesIcon,
} from '@/shared/ui';
import { isRrInterval } from '@/shared/lib/aiUtils';
import { DATA_HEATMAP_STATUS } from '@/shared/model';
import ellipsisIcon from './ellipsis-icon.svg';
import ButtonAddEcgBookmark from './buttonAddEcgBookmark';
import ButtonAddNewEvent from './buttonAddNewEvent';
import { calculateBeatReviewProgress } from './reviewBeatsHelper';
import useLeftColumn from './useLeftColumn';
import useRrIntervalMenu from './useRrIntervalMenu';

const { Panel } = Collapse;

// react-contextmenu's @types declare id/onClick/data narrower than how this file uses them - cast to any.
const ContextMenuAny = ContextMenu as any;
const MenuItemAny = MenuItem as any;

/**
 * Renders the bulk-action context menu for a left-column tab, varying by whether the tab is a beat type. Beat tabs offer mark all as reviewed and unreviewed, other tabs offer mark all as artifact plus delete all events when canDelete and reset all changes when hasChanged, each item dispatching through the trigger's onItemMenuClick.
 *
 * @param props - Menu props carrying the menu id and the trigger data.
 * @returns The rendered context menu for the triggering tab.
 */
const DynamicMenu = ({
  id,
  trigger,
}: {
  id: string;
  trigger?: {
    beatType?: any;
    canDelete?: boolean;
    hasChanged?: boolean;
    onItemMenuClick?: (e: any, data: any) => void;
  };
}) => {
  const onItemMenuClick = trigger ? trigger.onItemMenuClick : null;
  return (
    <ContextMenuAny id={id}>
      {trigger?.beatType ? (
        <>
          <MenuItemAny onClick={onItemMenuClick} data={{ action: 'markedAllReviewed' }}>
            Mark all as reviewed
          </MenuItemAny>
          <MenuItemAny onClick={onItemMenuClick} data={{ action: 'markedAllUnreviewed' }}>
            Mark all as unreviewed
          </MenuItemAny>
        </>
      ) : (
        <>
          {trigger?.canDelete && (
            <MenuItemAny onClick={onItemMenuClick} data={{ action: 'delete' }}>
              Delete all events
            </MenuItemAny>
          )}
          <MenuItemAny onClick={onItemMenuClick} data={{ action: 'markedAsArtifact' }}>
            Mark all as artifact
          </MenuItemAny>
          {trigger?.hasChanged && (
            <MenuItemAny onClick={onItemMenuClick} data={{ action: 'resetAllChanges' }}>
              Reset all changes
            </MenuItemAny>
          )}
        </>
      )}
    </ContextMenuAny>
  );
};

const MENU_ID = 'BEATHR';
const ConnectedMenu = connectMenu(MENU_ID)(DynamicMenu);
/**
 * Passes the context-menu trigger props straight through to the menu. Serves as the trigger's collect callback so its data reaches the menu items unchanged.
 *
 * @param props - The trigger props to hand to the menu.
 * @returns The same props object it was given.
 */
const collectData = (props: any) => props;

const RrIntervalMenu = ({
  activeButton,
  onClickTab,
}: {
  activeButton: any;
  onClickTab: (tab: any) => void;
}) => {
  const {
    dataHeatmapChartStatus,
    rrOverviewOrigin,
    bulkReviewedBeats,
    resetBulkReviewedBeats,
    reviewedBeatsChanges,
    onItemMenuClick,
    handleClickOption,
  } = useRrIntervalMenu();

  return (
    <div className="templates-btn-container">
      {_.map(RR_INTERVAL_MENU, (d: any, i: any) => {
        const { reviewedProgress, isChangeReviewd } = calculateBeatReviewProgress({
          beatChanges: reviewedBeatsChanges,
          bulkReviewedBeats,
          resetBulkReviewedBeats,
          rrOverviewOrigin,
          type: d.id,
        });

        const disableMenu = (dataHeatmapChartStatus as any)[d.id] !== DATA_HEATMAP_STATUS.SUCCESS;
        const dataProps = {
          id: MENU_ID,
          disable: disableMenu,
          beatType: d.id,
          onItemMenuClick,
          collect: collectData,
          reviewedProgress,
        };
        return (
          <ContextMenuTrigger {...dataProps}>
            <button
              key={d.id}
              id={d.id}
              name={d.name}
              className={classnames(
                'templates-btn --beat',
                activeButton === d.id ? '--active' : '',
              )}
              onClick={() => onClickTab(d.id)}
            >
              <OverviewCircularProgress
                isActive={activeButton === d.id}
                name="rrInterval"
                index={i}
                value={reviewedProgress as any}
                minDisplayText={0.1}
                minDisplay={6}
              />
              {d?.name}
              {!disableMenu && (
                <button
                  id={`beats-rr-interval-options-btn-${d.id}`}
                  className="options-btn"
                  onClick={(e) => handleClickOption(e, dataProps)}
                >
                  <img src={ellipsisIcon} alt="ellipsis icon" />
                </button>
              )}
              {isChangeReviewd && (
                <img
                  src={unappliedChangesIcon}
                  alt="Unapplied changes icon"
                  className="unapplied-changes-indicator"
                />
              )}
            </button>
          </ContextMenuTrigger>
        );
      })}
    </div>
  );
};

/**
 * Renders the beats tab's left navigation column of event categories. Shows the artifact-areas button, the AFib tab from renderAfibTab, a collapsible R-R heatmap panel highlighted while an R-R interval type is active, the connected bulk-action menu, and the add-event and add-bookmark buttons pinned at the bottom, reporting scroll through handleScrollLeftColumn.
 *
 * @returns The rendered left column with its category buttons and footer actions.
 */
const LeftColumn = () => {
  const {
    activeButton,
    eventChanges,
    groupEventChanges,
    eventsInfo,
    onClickTab,
    handleScrollLeftColumn,
    hasGroupTypeChangeTabInfos,
    hasUnappliedChangeTabInfos,
    onItemMenuClick,
  } = useLeftColumn();

  // react-contextmenu's ContextMenu always renders its `nav[role="menu"]`
  // (hidden via opacity, not display:none) and forwards no aria-label prop,
  // so it is otherwise nameless to assistive tech. Runs after every render —
  // cheap (one scoped query) and a no-op once the attribute is set.
  useEffect(() => {
    const menu = document.getElementById('left-column')?.querySelector('nav[role="menu"]');
    if (menu && !menu.getAttribute('aria-label')) {
      menu.setAttribute('aria-label', 'Event tab actions');
    }
  });

  /**
   * Renders the AFib entry in the beats left column with its review progress and event count. Reads the AFib count and progress out of eventsInfo, disables the context menu when the count is zero, caps the displayed count at 99 plus with a tooltip carrying the exact number, and shows an indicator when the tab has unapplied or group-type changes.
   *
   * @returns The rendered AFib tab button wrapped in its context-menu trigger.
   */
  const renderAfibTab = useCallback(() => {
    const eventType = TYPE_RHYTHM_EVENT_ENUM.AFIB;
    const formatValue = EVENT_COUNT_CONVERT[eventType];
    const eventCount = (eventsInfo as any)[formatValue].count;
    const groupTypeChange = !!hasGroupTypeChangeTabInfos[eventType];
    const disableMenu = eventCount === 0;
    const dataProps = {
      id: MENU_ID,
      disable: disableMenu,
      canDelete: true,
      hasChanged: groupTypeChange,
      eventType,
      onItemMenuClick,
      collect: collectData,
    };
    return (
      <ContextMenuTrigger {...dataProps}>
        <button
          id={eventType}
          name={eventType}
          className={classnames('templates-btn', activeButton === eventType ? '--active' : '')}
          onClick={() => {
            onClickTab(eventType);
          }}
        >
          <OverviewCircularProgress
            isActive={activeButton === eventType}
            name={`event-${eventType}`}
            index={1}
            value={(eventsInfo as any)[formatValue].progress}
          />
          AFib events
          <div className="ml-auto" id={`eventCountBeatHr${eventType}`}>
            <div className="event-count">{eventCount > 99 ? '99+' : eventCount}</div>
            {eventCount > 99 && (
              <UncontrolledTooltip
                className="custom-tooltip"
                target={`eventCountBeatHr${eventType}`}
                placement="bottom-end"
                delay={{ show: 0, hide: 0 }}
                hideArrow
              >
                {eventCount}
              </UncontrolledTooltip>
            )}
          </div>
          {(!!hasUnappliedChangeTabInfos[eventType] || groupTypeChange) && (
            <img
              src={unappliedChangesIcon}
              alt="Unapplied changes icon"
              className="unapplied-changes-indicator"
            />
          )}
        </button>
      </ContextMenuTrigger>
    );
  }, [groupEventChanges, eventsInfo, activeButton, eventChanges]);

  return (
    <div id="left-column" className="left-column" onScroll={handleScrollLeftColumn}>
      <div className="column-content">
        <button
          id="beats-artifact-areas-tab-btn"
          className={classnames(
            'templates-btn',
            activeButton === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ? '--active' : '',
          )}
          onClick={() => {
            onClickTab(TYPE_RHYTHM_EVENT_ENUM.ARTIFACT);
          }}
        >
          <img className="tab-icon" src={blackNoiseIcon} alt="Noise icon" />
          Artifact areas
          {!!hasUnappliedChangeTabInfos[TYPE_RHYTHM_EVENT_ENUM.ARTIFACT] && (
            <img
              src={unappliedChangesIcon}
              alt="Unapplied changes icon"
              className="unapplied-changes-indicator"
            />
          )}
        </button>
        <div className="left-column-divider" />
        {renderAfibTab()}
        <div className="left-column-divider" />
        <Collapse
          defaultActiveKey={['1']}
          expandIconPosition="start"
          expandIcon={({ isActive }) => (
            <img src={isActive ? arrowExpand : arrowCollapse} alt="arrow icon" />
          )}
        >
          <Panel
            header="R-R heatmap"
            key="1"
            className={classnames(
              'panel-header',
              isRrInterval(activeButton as any) ? '--active' : '',
            )}
          >
            <RrIntervalMenu activeButton={activeButton} onClickTab={onClickTab} />
          </Panel>
        </Collapse>
        <ConnectedMenu />
      </div>
      <div className="function-btn">
        <ButtonAddNewEvent />
        <ButtonAddEcgBookmark />
      </div>
    </div>
  );
};

export default LeftColumn;
